import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { initAuthenticationState, AuthenticationContext } from "userful-chronos-common-store/dist/auth";
import { useMount, useSetState } from "react-use";
import { MessageDefinition } from "userful-chronos-app-common-js/dist/message/messageModel";
import {
    getGlobalStates,
    setGlobalStateData,
    reSetGlobalStateData,
    initPostLogin,
    requestInitStatus,
    setEventBus,
    setSystemID,
    apiPost,
    apiGet,
    isDevEnvironment,
} from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { AxiosResponse } from "axios";
import Keycloak, { KeycloakConfig } from "keycloak-js";
import { ChronosEventBus2 } from "userful-chronos-app-common-js/dist/eventbus/eventbus2";
import { InitAppStatus } from "userful-chronos-app-common-js/dist/models/preLoginData";
import { REQUEST_POST_LOGIN } from "userful-chronos-app-common-js/dist/message/messageTypeDefinitions/request/login";
import { RESPOND_POST_LOGIN } from "userful-chronos-app-common-js/dist/message/messageTypeDefinitions/respond/login";
import { registerMsgHandler } from "userful-chronos-app-common-js/dist/message/messageRegistery";
import { AppUser } from "userful-chronos-app-common-js/dist/models/postLoginData";
import { KeycloakUserSession } from "userful-chronos-app-common-js/dist/models/keycloak-user-session";
import { userPermissions, getUserRoles } from "userful-chronos-app-common-js/dist/authenticataion/roles";
import { StringID } from "userful-chronos-app-common-js/dist/models/common";
import { redirectUriFor, ROUTE_PATH } from "userful-chronos-app-common-js/dist/routing";
import InitLoading from "../Widgets/InitLoading/InitLoading";
import { UserData } from "userful-chronos-app-common-js/dist/models/usermgt/userData";
import {
    GET_CLEARANCE_DATA,
    GET_CLEARANCE_ACTION,
} from "userful-chronos-app-common-js/dist/models/usermgt/clearanceLevel";
import { KeycloakFederatedEntity, KeycloakUserID } from "userful-chronos-app-common-js/dist/models/usermgt/usermgt";
import { SocialProviderConfig } from "userful-chronos-app-common-js/dist/models/usermgt/socialIdentityProviders";
import HTTPError from "./HTTPError";

const GET_AETHER_ID = "/aether/open/systemID";
const AUTHORIZE_USER = "/aether/auth/kc/user/authorize";
const UN_AUTHORIZE_USER = "/aether/auth/kc/user/unauthorize";
const GET_USER = "/aether/auth/kc/user";
const GET_SOCIAL_IDENTITY_PROVIDER_CONFIG = "/aether/auth/kc/idp/config";

export const authUser = (token: string, keycloakUserSession: KeycloakUserSession) =>
    apiPost(`${getGlobalStates().host}${AUTHORIZE_USER}`, keycloakUserSession, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

export const getUser = (token: string, keycloakUserID: KeycloakUserID) =>
    apiPost(`${getGlobalStates().host}${GET_USER}`, keycloakUserID, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

export const getSocialIdentityProviderConfig = (token: string) =>
    apiGet(`${getGlobalStates().host}${GET_SOCIAL_IDENTITY_PROVIDER_CONFIG}`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

export const unAuthUser = (token: string, keycloakUserSession: KeycloakUserSession) =>
    apiPost(`${getGlobalStates().host}${UN_AUTHORIZE_USER}`, keycloakUserSession, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

const getSystemID = () => apiGet(`${getGlobalStates().host}${GET_AETHER_ID}`);

export const loadClearanceData = (token: string) => {
    const t1 = getGlobalStates().host + GET_CLEARANCE_DATA;

    return apiGet(t1, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
};

export const loadClearanceActions = (token: string) => {
    const t1 = getGlobalStates().host + GET_CLEARANCE_ACTION;

    return apiGet(t1, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
};

export const checkReponseIsValid = (name: string, response: AxiosResponse): boolean => {
    if (response.status >= 400) {
        console.log(`${name} error: ${response.status}`);
        return false;
    }
    if (!response.data || response.data.severity !== "SUCCESS") {
        console.log(`${name} empty response`);
        return false;
    }
    return true;
};

const isUsingHttp = () => {
    return !isDevEnvironment() && getGlobalStates().host && getGlobalStates().host.toLowerCase().startsWith("http:");
};

interface IProps {
    children: ReactNode;
    authenticate?: boolean;
}

export const AuthenticationProvider: React.FC<IProps> = ({ children, authenticate = true }) => {
    const [showChildren, setShowChildren] = useState(!authenticate);
    const [loggingOut, setLoggingOut] = useState(false);

    const [state, setState] = useSetState({
        ...initAuthenticationState,
    });
    const tryLoginInterval = useRef(null);
    const tryLoginAtInterval = () => {
        clearInterval(tryLoginInterval.current);
        tryLoginInterval.current = setInterval(login, 3000);
    };
    const getPostLoginDataInterval = useRef(null);
    const getPostLoginDataTask = () => {
        if (isUsingHttp()) {
            return;
        }
        clearInterval(getPostLoginDataInterval.current);
        getPostLoginDataInterval.current = setInterval(() => {
            sendMsg(REQUEST_POST_LOGIN, {
                clientAppName: getGlobalStates().appID.value,
            });
        }, 2000);
    };

    const pendoInitialized = useRef(false);

    useEffect(() => {
        if (isUsingHttp()) {
            return;
        }
        if (showChildren && authenticate && !tryLoginInterval.current && !isUserAuthorized()) {
            authenticateUser();
        }
    }, [authenticate]);

    useMount(() => {
        if (isUsingHttp()) {
            return;
        }
        registerMsgHandler(RESPOND_POST_LOGIN, (result) => {
            console.debug("Received post login data");
            clearInterval(getPostLoginDataInterval.current);
            const data = JSON.parse(result);
            setState({ postLoginData: data });
            initPostLogin(data);
        });
        authenticateUser();
    });

    useEffect(() => {
        if (isUsingHttp()) {
            return;
        }
        const { connected, postLoginData, canReconnect } = state;
        const authorized = isUserAuthorized();
        if (authorized) {
            clearInterval(tryLoginInterval.current);
            tryLoginInterval.current = null;
        }
        if (postLoginData && !showChildren) {
            setShowChildren(true);
        }
        if (authorized && !connected) {
            if (canReconnect) {
                connectEventbus();
            } else {
                authenticateUser();
            }
        }
    }, [state, showChildren]);

    const initPendo = (userData: AppUser, keycloak: Keycloak) => {
        if (!pendoInitialized.current) {
            // @ts-ignore
            if (!window.pendo) {
                console.warn("pendo not available!");
                return;
            }
            const whatfixUserID = `${getGlobalStates().systemID}:${userData.userName}`;
            window.sessionStorage.setItem("whatfixUserID", whatfixUserID);
            window.sessionStorage.setItem("roles", getUserRoles(keycloak).join(","));
            const pendoData = {
                visitor: {
                    id: whatfixUserID, // Required if user is logged in
                    // userEmail: keycloak.tokenParsed?.email || '',
                    // fullName: `${keycloak.tokenParsed?.given_name || ""}${keycloak.tokenParsed?.family_name? ` ${keycloak.tokenParsed?.family_name}` : "" }`,
                    roles: getUserRoles(keycloak),
                    // You can add any additional visitor level key-values here,
                    // as long as it's not one of the above reserved names.
                    platform: "Infinity",
                    platformVerion: userData.version,
                    organizationName: userData.organization,
                },

                account: {
                    id: getGlobalStates().systemID, // Highly recommended, required if using Pendo Feedback
                    // name:         // Optional
                    // is_paying:    // Recommended if using Pendo Feedback
                    // monthly_value:// Recommended if using Pendo Feedback
                    // planLevel:    // Optional
                    // planPrice:    // Optional
                    // creationDate: // Optional
                    // You can add any additional account level key-values here,
                    // as long as it's not one of the above reserved names.
                    platformVerion: userData.version,
                },
            };
            // @ts-ignore
            window.pendo.initialize(pendoData);
            pendoInitialized.current = true;
            console.info("pendo initialized");
        }
    };

    const authenticateUser = () => {
        if (getGlobalStates().keycloak && getGlobalStates().keycloak.authenticated && isUserAuthorized()) {
            console.debug(`Keycloak user = ${getGlobalStates().keycloak.tokenParsed.preferred_username}`);
            authorizeUser(getGlobalStates().keycloak);
        } else if (authenticate) {
            console.debug("authentication required");
            login();
            tryLoginAtInterval();
        }
    };

    const isUserAuthorized = () => {
        const { keycloak, userData } = state;

        if (keycloak === null || !keycloak.authenticated || userData === null) {
            return false;
        }

        if (
            userData.userID === null ||
            userData.userID.value !== keycloak.tokenParsed.sub ||
            userData.userSessionID === null ||
            userData.userSessionID !== keycloak.tokenParsed.sid
        ) {
            return false;
        }

        return true;
    };

    const reconnect = () => {
        if (!state.keycloak || !state.keycloak.authenticated) {
            handleUserNotAuthorized();
        } else {
            setState({ connected: false });
        }
    };

    const connectEventbus = () => {
        const { userData } = state;

        console.debug("connectEventbus - user = '", userData.userName + "', appId: " + getGlobalStates().appID.value);

        const eventbus = new ChronosEventBus2(
            userData.systemID.value,
            userData.userID.value,
            userData.userSessionID,
            userData.connectionID,
        );
        eventbus
            .get()
            .then((ev) => {
                setEventBus(ev);
                setState({
                    eventbus: ev,
                    connected: true,
                });
                ev.connected = true;
                ev.sendMsg(REQUEST_POST_LOGIN, {
                    clientAppName: getGlobalStates().appID.value,
                });
                getPostLoginDataTask();
                ev.onClose = (e) => {
                    ev.connected = false;
                    setTimeout(reconnect, 500);
                };
            })
            .catch((e) => {
                console.error("Error connecting eventbus", e);
                setTimeout(authenticateUser, 5000);
            });
    };

    const handleUserfulVersion = (versionString: string) => {
        try {
            const versionParts = versionString.split(".");
            if (versionParts.length > 0) {
                globalThis.userfulVersionMajor = Number(versionParts[0]);
            }
            if (versionParts.length === 2) {
                globalThis.userfulVersionMinor = Number(versionParts[1]);
            } else if (versionParts.length > 2) {
                globalThis.userfulVersionMinor = Number(`${versionParts[1]}.${versionParts[2]}`);
            }
            globalThis.userfulVersion = versionString;
        } catch (e) {
            console.debug("Cannot parse userful version", e);
        }
    };

    const loadUserFromServer = async (keycloak: Keycloak, userID: KeycloakUserID) => {
        try {
            const userResponse = await getUser(keycloak.token, userID);
            const isValidResponse = checkReponseIsValid("User request", userResponse);

            return isValidResponse ? userResponse.data.arg : null;
        } catch (error) {
            console.warn("failed to load user", error);
            return null;
        }
    };

    const loadSocialIdentityProviderConfigDataFromServer = async (keycloak: Keycloak) => {
        try {
            const socialIdentityProviderConfigResp = await getSocialIdentityProviderConfig(keycloak.token);
            const isValidSocialIdentityProviderConfigResp = checkReponseIsValid(
                "Social config request",
                socialIdentityProviderConfigResp,
            );
            const socialProviderConfigs = isValidSocialIdentityProviderConfigResp
                ? socialIdentityProviderConfigResp.data.arg
                : [];

            return socialProviderConfigs as SocialProviderConfig[];
        } catch (error) {
            console.warn("failed", error);
            return [];
        }
    };

    const loadUserDataFromServer = async (keycloak: Keycloak, userID: KeycloakUserID) => {
        try {
            const actionsResponse = await loadClearanceActions(keycloak.token);
            const clearanceDataResponse = await loadClearanceData(keycloak.token);

            const actionsIsValid = checkReponseIsValid("authorize", actionsResponse);
            const clearanceDataIsValid = checkReponseIsValid("authorize", clearanceDataResponse);

            const userResponse = await getUser(keycloak.token, userID);
            const isValidUserResponse = checkReponseIsValid("User request", userResponse);

            const socialIdentityProviderConfigResp = await getSocialIdentityProviderConfig(keycloak.token);
            const isValidSocilIdentityProviderConfigResp = checkReponseIsValid(
                "Social config request",
                socialIdentityProviderConfigResp,
            );

            const actions = actionsIsValid ? actionsResponse.data.arg : [];
            const clearanceData = clearanceDataIsValid ? clearanceDataResponse.data.arg : [];
            const user = isValidUserResponse ? userResponse.data.arg : null;
            const socialProviderConfigs = isValidSocilIdentityProviderConfigResp
                ? socialIdentityProviderConfigResp.data.arg
                : [];

            return { actions, clearanceData, user, socialProviderConfigs } as UserData;
        } catch (error) {
            console.warn("failed", error);
            return { actions: [], clearanceData: [], user: null, socialProviderConfigs: [] } as UserData;
        }
    };

    const handleUserAuthorized = (userData: AppUser, keycloak: Keycloak) => {
        clearInterval(tryLoginInterval.current);
        tryLoginInterval.current = null;
        console.debug(`Authorized user = ${userData.userName}`);
        initPendo(userData, keycloak);
        loadUserDataFromServer(keycloak, userData.userID).then((data) => {
            const { actions, clearanceData, user, socialProviderConfigs } = data as UserData;
            const userSecurityPermissions = keycloak.idTokenParsed.AccessPermission || [];
            const userSecurityDataPermissions = clearanceData.filter((d) =>
                userSecurityPermissions.includes(d.clearanceLevel.name),
            );
            const permissions = userPermissions(userData.capabilities, userSecurityDataPermissions, keycloak);
            // if (!permissions.isAdminUser) {
            //     localStorage.removeItem("authData");
            // }
            localStorage.removeItem("authData");
            setGlobalStateData(userData, permissions, keycloak, actions, clearanceData, user, socialProviderConfigs);
            setState({
                keycloak: keycloak,
                canReconnect: true,
                connected: false,
                userData: userData,
                actions: actions,
                clearanceData: clearanceData,
                userPermission: permissions,
                user: user,
                socialProviderConfigs: socialProviderConfigs,
            });
            handleUserfulVersion(userData.version);
            console.debug(socialProviderConfigs);
            console.debug(user);
            console.debug(permissions);
        });
    };

    const handleUserNotAuthorized = () => {
        reSetGlobalStateData();
        setState({
            keycloak: null,
            canReconnect: true,
            connected: false,
            userData: null,
            userPermission: {},
            user: null,
            socialProviderConfigs: [],
        });

        clearInterval(tryLoginInterval.current);
        tryLoginInterval.current = null;
        setTimeout(() => authenticateUser(), 5000);
    };

    const authorizeUser = (keycloak: Keycloak) => {
        const keycloakUserSession = {
            userID: keycloak.tokenParsed.sub,
            userName: keycloak.tokenParsed.preferred_username,
            userSessionID: keycloak.tokenParsed.sid,
        } as KeycloakUserSession;
        authUser(keycloak.token, keycloakUserSession)
            .then((response) => {
                if (response.status >= 400) {
                    console.log("Error authorizing user: " + response.status);
                    handleUserNotAuthorized();
                } else if (!response.data) {
                    console.log("Error authorizing user: empty response");
                    handleUserNotAuthorized();
                } else if (response.data.severity !== "SUCCESS") {
                    console.log(`authorization failure`, response.data.unlocalizedMsg);
                    handleUserNotAuthorized();
                } else {
                    const userData = response.data.arg as AppUser;
                    handleUserAuthorized(userData, keycloak);
                    console.debug(`Authorized user = ${keycloak.tokenParsed.preferred_username}`);
                }
            })
            .catch((e) => {
                console.error("Error authorizing user ", e);
                handleUserNotAuthorized();
            });
    };

    const handleKeycloakInit = (keycloak: Keycloak) => {
        if (!keycloak.authenticated) {
            handleUserNotAuthorized();
        } else {
            console.debug(`Authenticated user = ${keycloak.tokenParsed.preferred_username}`);
            authorizeUser(keycloak);
        }
    };

    const initKeycloak = (initStatus: InitAppStatus) => {
        console.debug(`initKeycloak`);
        const redirectUri = redirectUriFor(getGlobalStates().appID.value);
        const keycloakConfig: KeycloakConfig = {
            url: `${getGlobalStates().host}${initStatus.keycloakAuthPath}`,
            realm: initStatus.realmName,
            clientId: initStatus.clientID,
        };
        const keycloak = new Keycloak(keycloakConfig);
        keycloak.onAuthSuccess = () => {
            console.debug("onAuthSuccess");
        };
        keycloak.onAuthRefreshSuccess = () => {
            console.debug("onAuthRefreshSuccess");
        };
        keycloak.onAuthRefreshError = () => {
            console.debug("onAuthRefreshError");
        };
        keycloak
            .init({
                onLoad: "login-required",
                redirectUri: redirectUri,
                enableLogging: true,
                checkLoginIframe: !isDevEnvironment(),
                checkLoginIframeInterval: 5,
                flow: isDevEnvironment() ? "standard" : "hybrid",
                useNonce: !isDevEnvironment(),
            })
            .then(() => {
                handleKeycloakInit(keycloak);
            })
            .catch((e) => {
                console.error(`Error initializing keycloak`, e);
            });
    };

    const handleReceiveInitStatus = (response: AxiosResponse) => {
        if (response.status >= 400) {
            console.log("Error getting service-info: " + response.status);
        } else if (!response.data) {
            console.log("Error getting service-info: empty response");
        } else if (response.data.severity !== "SUCCESS") {
            console.log(`handleReceiveInitStatus failure`, response.data.unlocalizedMsg);
        } else {
            const initStatus = response.data.arg as InitAppStatus;
            if (initStatus.setupRequired) {
                window.location.replace(ROUTE_PATH.ADMIN.SETUP);
            } else {
                if (isUsingHttp()) {
                    clearInterval(tryLoginInterval.current);
                    tryLoginInterval.current = null;
                    return;
                }
                console.debug(`handleReceiveInitStatus`, initStatus);
                initKeycloak(initStatus);
            }
        }
    };

    const login = () => {
        console.debug(`login`);
        if (!getGlobalStates().isReady) {
            console.log("GlobalStates not initialized: ", getGlobalStates());
        } else {
            processLogin();
        }
    };

    const processLogin = () => {
        getSystemID()
            .then((response) => {
                if (response.status >= 400) {
                    console.error("Cannot login. Error getting systemID: " + response.status);
                } else if (!response.data) {
                    console.error("Cannot login. Error getting systemID: empty response");
                } else {
                    const systemID = response.data as StringID;
                    setSystemID(systemID.value);
                    requestInitStatus()
                        .then(handleReceiveInitStatus)
                        .catch((e) => {
                            console.error("Cannot login. Error retrieving service info", e);
                        });
                }
            })
            .catch((e) => {
                console.error("Cannot login. Error retrieving systemID", e);
            });
    };

    const getMicrosoftLogoutUrl = (
        keycloakLogoutUrl: string,
        microsoftFederatedEntities: KeycloakFederatedEntity[],
        socialProviderConfigs: SocialProviderConfig[],
    ) => {
        if (microsoftFederatedEntities.length > 0) {
            const microsoftConfigs = socialProviderConfigs.filter((c) => c.providerId === "microsoft");
            if (microsoftConfigs.length > 0) {
                const config = microsoftConfigs[0];
                const redirectUri = "https://login.microsoftonline.com/"
                    .concat(config.tenantId)
                    .concat("/oauth2/v2.0/logout");

                return redirectUri.concat("?post_logout_redirect_uri=").concat(encodeURIComponent(keycloakLogoutUrl));
            }
        }

        return null;
    };

    const federatedEntityLogoutUrl = async (keycloakLogoutUrl: string) => {
        let socialProviderConfigs = getGlobalStates().socialProviderConfigs;
        if (socialProviderConfigs.length == 0) {
            socialProviderConfigs = await loadSocialIdentityProviderConfigDataFromServer(getGlobalStates().keycloak);
        }

        let user = getGlobalStates().user;
        const userData = getGlobalStates().userData;
        if (user == null && userData != null && userData.userID != null) {
            user = await loadUserFromServer(getGlobalStates().keycloak, userData.userID);
        }

        if (user !== null && user.federatedEntities.length > 0) {
            const microsoftFederatedEntities = user.federatedEntities.filter(
                (f) => f.identityProvider === "microsoft" && f.userName === user.userProfile.userName,
            );
            if (microsoftFederatedEntities.length > 0) {
                const microsoftLogoutUrl = getMicrosoftLogoutUrl(
                    keycloakLogoutUrl,
                    microsoftFederatedEntities,
                    socialProviderConfigs,
                );
                if (microsoftLogoutUrl != null) {
                    return microsoftLogoutUrl;
                }
            }
        }
        return null;
    };

    const buildKeycloakLogoutUrl = () => {
        const baseUrl = getGlobalStates().host;

        const keycloakLogoutUrl = baseUrl
            .concat("/auth/realms/")
            .concat(state.keycloak.realm)
            .concat("/protocol/openid-connect/logout?")
            .concat("client_id=")
            .concat(state.keycloak.clientId);

        let keycloakLogoutUrlWithPostRedirect = keycloakLogoutUrl
            .concat("&post_logout_redirect_uri=")
            .concat(encodeURIComponent(baseUrl));

        const idToken = state.keycloak.idToken;
        if (idToken !== null && idToken != "" && idToken != undefined) {
            keycloakLogoutUrlWithPostRedirect = keycloakLogoutUrlWithPostRedirect
                .concat("&id_token_hint=")
                .concat(idToken);
        }

        const nonce = state.keycloak.tokenParsed.nonce;
        if (nonce !== null && nonce != "" && nonce != undefined) {
            keycloakLogoutUrlWithPostRedirect = keycloakLogoutUrlWithPostRedirect.concat("&nonce=").concat(nonce);
        }

        const iss = state.keycloak.tokenParsed.iss;
        if (iss !== null && iss != "" && iss != undefined) {
            keycloakLogoutUrlWithPostRedirect = keycloakLogoutUrlWithPostRedirect.concat("&issuer=").concat(iss);
        }

        return keycloakLogoutUrlWithPostRedirect;
    };

    const processKeycloakLogout = async () => {
        const keycloakLogoutUrl = buildKeycloakLogoutUrl();
        const federatedLogoutUrl = await federatedEntityLogoutUrl(keycloakLogoutUrl);
        cleanupUserStateOnKeycloakLogout();
        if (federatedLogoutUrl != null) {
            window.location.href = federatedLogoutUrl;
        } else {
            window.location.href = keycloakLogoutUrl;
        }
    };

    const cleanupUserStateOnKeycloakLogout = () => {
        reSetGlobalStateData();
        setState({
            keycloak: null,
            canReconnect: true,
            userData: null,
            userPermission: {},
            user: null,
            socialProviderConfigs: [],
        });

        clearInterval(tryLoginInterval.current);
        tryLoginInterval.current = null;
    };

    const processAetherLogout = () => {
        console.debug("Logout from aether");
        const keycloakUserSession = {
            userID: state.userData.userID.value,
            userName: state.userData.userName,
            userSessionID: state.userData.userSessionID,
        } as KeycloakUserSession;
        unAuthUser(state.keycloak.token, keycloakUserSession)
            .then((response) => {
                console.debug("Logout from keycloak");
                processKeycloakLogout();
            })
            .catch((e) => {
                console.warn("Error un-authorizing the user", e);
                console.debug("Logout from keycloak");
                processKeycloakLogout();
            });
    };

    const logout = () => {
        setLoggingOut(true);
        localStorage.removeItem("authData");
        if (!state.keycloak || !state.keycloak.authenticated) {
            handleUserNotAuthorized();
        } else {
            if (isUserAuthorized()) {
                processAetherLogout();
            } else {
                processKeycloakLogout();
            }
        }
    };

    const sendMsg = (msgDef: MessageDefinition, msg?: Object | string | number | boolean) => {
        const eventbus = getGlobalStates().eventBus;
        if (!eventbus) {
            console.info("Cannot send msg due to eventbus not open");
        } else {
            eventbus.sendMsg(msgDef, msg);
        }
    };

    const value = useMemo(
        () => ({
            state,
            setState,
            login,
            logout,
            sendMsg,
        }),
        [state, setState],
    );

    if (authenticate && isUsingHttp()) {
        return <HTTPError />;
    }
    return (
        <AuthenticationContext.Provider value={value}>
            <>{showChildren && !loggingOut ? children : <InitLoading />}</>
        </AuthenticationContext.Provider>
    );
};
